'use client';

import { useCallback } from 'react';
import { useApolloClient } from '@frameio/graphql-client';
import { plural } from '@frameio/i18n';
import { useNotificationsOptIn } from '@frameio/notifications/hooks';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useTrack } from '@frameio/shared/segment/hooks';
import { ToastAlert, useToast } from '@frameio/vapor';
import { useCreateUbsValidateAssetOperationMutation } from '../gql/mutations/generated/CreateUbsValidateAssetOperation';
import {
  GetAssetsBrandValidationMetadata,
  type GetAssetsBrandValidationMetadataQuery,
  type GetAssetsBrandValidationMetadataQueryVariables,
} from '../gql/queries/generated/GetAssetsBrandValidationMetadata';
import {
  toBrandValidationInputs,
  validateBrandValidationAssets,
} from '../utils';
import TEXT from './generated/TEXT/useUbsValidationAction';
import { useErrorToast } from './useErrorToast';

const TOAST_DURATION = 3000;

TEXT.DEFINE(
  {
    /** Success toast message when UBS validation starts; pluralized by file count. */
    validationStarted: (fileCount: number) =>
      plural(fileCount, {
        one: `UBS validation started for ${fileCount} file.`,
        other: `UBS validation started for ${fileCount} files.`,
      }),
    /** Appended to the success toast when unsupported assets were skipped; pluralized by skipped count. */
    unsupportedSkipped: (skippedCount: number) =>
      plural(skippedCount, {
        one: `${skippedCount} unsupported file was skipped.`,
        other: `${skippedCount} unsupported files were skipped.`,
      }),
    /** Message shown when the selection exceeds the supported limit for UBS validation. */
    tooManySelectedText:
      'UBS validation can be applied to up to 100 assets at a time.',
    /** Message shown when we fail to start UBS validation. */
    errorStartingText: 'There was an error starting the UBS validation.',
    /** Message shown when no selected files match the supported formats. */
    noSupportedFilesText: 'None of the selected files have a supported format.',
  },
  { plural }
);

const MAX_ASSETS_FOR_VALIDATE_ASSET = 100;

type ValidateAssetArgs = {
  assetIds: string[];
  brandId: string;
  category?: string;
  totalSelectedCount: number;
};

export function useUbsValidationAction() {
  const [createUbsValidateAssetOperation] =
    useCreateUbsValidateAssetOperationMutation();
  const apolloClient = useApolloClient();
  const accountId = useActiveAccountId();
  const showErrorToast = useErrorToast();
  const { addToast } = useToast();
  const track = useTrack();
  const { maybeShowNotificationsOptInToast } = useNotificationsOptIn();

  const tooManySelectedMessage = TEXT.tooManySelectedText;
  const startErrorMessage = TEXT.errorStartingText;
  const noSupportedFilesMessage = TEXT.noSupportedFilesText;
  const validationStartedMessage = TEXT.validationStarted;
  const unsupportedSkippedMessage = TEXT.unsupportedSkipped;

  const validateAssetUbs = useCallback(
    async ({
      assetIds,
      brandId,
      category,
      totalSelectedCount,
    }: ValidateAssetArgs): Promise<boolean> => {
      if (!assetIds?.length) return false;
      if (assetIds.length > MAX_ASSETS_FOR_VALIDATE_ASSET) {
        showErrorToast(tooManySelectedMessage);
        return false;
      }

      if (!accountId) {
        showErrorToast(startErrorMessage);
        return false;
      }

      const { data } = await apolloClient.query<
        GetAssetsBrandValidationMetadataQuery,
        GetAssetsBrandValidationMetadataQueryVariables
      >({
        query: GetAssetsBrandValidationMetadata,
        variables: { accountId, assetIds, first: assetIds.length },
        fetchPolicy: 'cache-first',
      });
      const inputs = toBrandValidationInputs(
        data?.account?.assets?.nodes ?? []
      );
      const { validAssetIds } = validateBrandValidationAssets(inputs);

      if (validAssetIds.length === 0) {
        showErrorToast(noSupportedFilesMessage);
        return false;
      }

      const { errors } = await createUbsValidateAssetOperation({
        variables: {
          input: {
            brandId,
            category,
            sourceAssetIds: validAssetIds,
          },
        },
      });
      if (errors?.length) {
        showErrorToast(startErrorMessage);
        return false;
      }
      const skippedCount = Math.max(
        0,
        totalSelectedCount - validAssetIds.length
      );
      track('brand-validation-started', {
        assets_count: validAssetIds.length,
      });
      maybeShowNotificationsOptInToast({
        action: 'brand_validation',
      });
      const startedText = String(
        validationStartedMessage(validAssetIds.length)
      );
      const toastTitle =
        skippedCount > 0
          ? `${startedText} ${String(unsupportedSkippedMessage(skippedCount))}`
          : startedText;
      addToast(
        (dismiss, closeLabel) => (
          <ToastAlert
            closeLabel={closeLabel}
            onRequestClose={dismiss}
            title={toastTitle}
            variant="success"
          />
        ),
        { duration: TOAST_DURATION }
      );
      return true;
    },
    [
      accountId,
      apolloClient,
      createUbsValidateAssetOperation,
      showErrorToast,
      addToast,
      track,
      maybeShowNotificationsOptInToast,
      tooManySelectedMessage,
      startErrorMessage,
      noSupportedFilesMessage,
      validationStartedMessage,
      unsupportedSkippedMessage,
    ]
  );
  return { validateAssetUbs };
}
